// Added by Sejune Oh on 2019-05-17
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { RefObject, FC } from 'react'
import { WithStyles, createStyles, Theme, withStyles } from '@material-ui/core'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import { FORM_ID, MgmtValidator, UILanguageProps, ReactSelectEvent, FormatterProps } from '@/helper/define'

// Intl
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Component
import Dialog, { DialogCProps } from '@/components/common/Dialog'
import Input from '@/components/common/Input'
import Select from '@/components/common/Select'
import { DisplableUserElementProps } from '@/stores/mgmt/user'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface UserConfigDialogFCProps extends WithStyles<typeof styles>, MgmtValidator, FormatterProps {
    dialog: UserConfigDialogProps
    usernameRef: RefObject<any>
    emailRef: RefObject<any>
    curPwdRef: RefObject<any>
    newPwdRef: RefObject<any>
    newConfirmPwdRef: RefObject<any>
    userElement: DisplableUserElementProps
    uiLanguage: UILanguageProps
    uiLanguageList: UILanguageProps[]
    onUILanguageChange: ReactSelectEvent
    isExternalUser: boolean
}

export interface UserConfigDialogProps extends DialogCProps {
    ref: RefObject<Dialog>
}

const UserConfigDialog: FC<UserConfigDialogFCProps> = ({
    validator,
    blurValidator,
    dialog,
    formatter,
    classes,
    ...props
}) => {
    const labelUserName = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialog.label_username' })
    const labelEmail = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialog.label_email' })
    const labelDispName = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialog.label_disp_name' })
    const labelUiLang = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialog.label_ui_lang' })
    const labelCurPwd = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialog.label_cur_password' })
    const labelNewPwd = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialog.label_new_password' })
    const labelConfirmPwd = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialog.label_confirm_password' })
    const dialogPaperStyle = { minWidth: 620, maxWidth: 640 }
    return (
        <Dialog
            ref={dialog.ref}
            dialogPaperStyle={dialogPaperStyle}
            onConfirm={dialog.onConfirm}
            onCancel={dialog.onCancel}
        >
            <form id={FORM_ID} className={classes.FormWrapper}>
                <Input
                    ref={props.usernameRef}
                    id={'username'}
                    type="string"
                    label={labelUserName}
                    isFullWidth={true}
                    required={true}
                    value={props.userElement.username}
                    disabled
                />
                <Input
                    id={'email'}
                    ref={props.emailRef}
                    type="string"
                    label={labelEmail}
                    isFullWidth={true}
                    required={true}
                    defaultValue={props.userElement.email}
                    validator={validator!.emailValidator}
                    blurValidator={blurValidator!.emailBlurValidator}
                    disabled={props.isExternalUser}
                    autoFocus={!props.isExternalUser}
                />
                <Input
                    id={'display_name'}
                    type="string"
                    label={labelDispName}
                    defaultValue={props.userElement.display_name}
                    isFullWidth={true}
                    required={true}
                    validator={validator!.displayNameValidator}
                    disabled={props.isExternalUser}
                />
                <Select
                    id="ui_language"
                    label={labelUiLang}
                    value={props.uiLanguage.id}
                    menuItems={props.uiLanguageList}
                    onChange={props!.onUILanguageChange}
                    isFullWidth={true}
                    isRequired={true}
                    itemFormatter={formatter!.uiLanguageFormatter}
                    disabled={props.isExternalUser}
                />

                <Input
                    ref={props.curPwdRef}
                    id={'current_pwd'}
                    label={labelCurPwd}
                    isFullWidth={true}
                    isPassword={true}
                    validator={validator!.curPwdValidator}
                    blurValidator={blurValidator!.curPwdBlurValidator}
                    disabled={props.isExternalUser}
                />
                <Input
                    ref={props.newPwdRef}
                    id={'password'}
                    label={labelNewPwd}
                    isFullWidth={true}
                    isPassword={true}
                    validator={validator!.pwdValidator}
                    blurValidator={blurValidator!.pwdBlurValidator}
                    disabled={props.isExternalUser}
                />
                <Input
                    ref={props.newConfirmPwdRef}
                    id={'confirm_pwd'}
                    label={labelConfirmPwd}
                    isFullWidth={true}
                    isPassword={true}
                    validator={validator!.confirmPwdValidator}
                    blurValidator={blurValidator!.confirmPwdBlurValidator}
                    disabled={props.isExternalUser}
                />
            </form>
        </Dialog>
    )
}
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Styles
const styles = (theme: Theme) =>
    createStyles({
        FormWrapper: {
            overflow: 'hidden',
        },
    })

export default withStyles(styles, { withTheme: true })(UserConfigDialog)
